import { useEffect, useRef, useState, useCallback, useMemo, useReducer } from 'react';
import { match } from 'ts-pattern';
import { useHeroCharacter } from '@/modules/hero-character/hooks/use-hero-character';
import type { GameEngine } from '@/game/engine';
import { GameSessionController } from './session-controller';
import type { RoadSegmentPayload } from '@/network/api';
import { bootstrapSession, completeNamedSession } from './session-bootstrap';
import {
  GamePhase,
  BuffType,
  type GameState,
  type NPCData,
  type AttackPayload,
  type EnemyRegenPayload,
} from '@/game/types';
import type { GameWebSocket } from '@/network/websocket';
import {
  sendTownTourNPCDialogClosed,
  sendTownTourNPCInteractionOpened,
  sendTownTourNPCInteractionClosed,
} from '@/modules/game-session/ws-bridge';
import {
  sendWorldEventAction,
  getWorldEventHandler,
  type ActiveWorldEvent,
  type WorldEventLocaleSnapshot,
} from '@/world-events';
import '@/world-events/handlers/questAlms';
import '@/world-events/handlers/socialPass';
import '@/world-events/handlers/roadPoi';
import '@/world-events/handlers/wild';
import {
  ackChangelog,
  getHeroQuests,
  getHeroEquipment,
  claimQuest,
  abandonQuest,
  getAchievements,
  getHero,
  defaultNpcShopBundle,
} from '@/network/api';
import type { HeroResponse, Achievement } from '@/network/api';
import type {
  AdventureLogEntry,
  Town,
  NPC,
  HeroQuest,
  EquipmentItem,
  TownTourPhasePayload,
} from '@/game/types';
import type { OfflineReport as OfflineReportData } from '@/network/api';
import { hapticImpact, hapticNotification, getTelegramUserId, getTelegramWebApp } from '@/shared/telegram';
import { useUiClock } from '@/hooks/useUiClock';
import {
  appendAdventureLogRawRow,
  buildAdventureLogEntriesFromRaw,
  type AdventureLogRawRow,
} from '@/modules/adventure-log/adventureLogMap';
import {
  isAdventureLogCombatCode,
  isAdventureLogEncounterCode,
  parseAdventureLogLine,
} from '@/modules/adventure-log/adventureLogMarkers';
import { readerJournal } from '@/modules/adventure-log/reader-journal';
import { thoughtText } from '@/modules/adventure-log/thought-copy';
import { crierAnnouncementText, parseCrierSpeechClock, type ActiveCrierSpeech, type CrierAnnouncement } from '@/modules/town-news/utils/crier-announcement';
import { CrierSpeechPresenter } from './crier-speech-presenter';
import { narrativeBubbleRemainingMs } from './narrative-bubble-timing';
import { formatAdventureLogEvent, formatAdventureLogPayload } from '@/modules/adventure-log/adventureLogFormat';
import type { AdventureLogLinePayload } from '@/game/types';
import { t, type Locale, type Translations } from '@/i18n';
import { townLabel } from '@/i18n/contentLabels';
import type { CombatOverlayEvent, CombatOverlayTarget } from '@/modules/combat-hud/components/CombatOverlay';
import type { HeroSheetTab } from '@/modules/hero-sheet/components/HeroSheetModal';
import {
  applyTownsToEngine,
  fetchTownLayoutDetails,
  townTourPayloadToNPCData,
} from '@/modules/game-session/town-mapper';
import { loadCooldownsFromStorage } from '@/modules/game-session/buff-cooldown';
import { handleBuffActivate as runBuffActivate } from '@/modules/game-session/buff-actions';
import {
  initialSessionStore,
  sessionReducer,
  type ChangelogOpen,
} from '@/modules/game-session/session-store';

export interface UseGameSessionOptions {
  locale: Locale;
  translations: Translations;
}

export function useGameSession({ locale, translations: tr }: UseGameSessionOptions) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const wsRef = useRef<GameWebSocket | null>(null);
  const controllerRef = useRef<GameSessionController | null>(null);

  const [gameState, setGameState] = useState<GameState>({
    phase: GamePhase.Walking,
    hero: null,
    enemy: null,
    loot: null,
    lastVictoryLoot: null,
    tick: 0,
    serverTimeMs: 0,
    routeWaypoints: null,
  });

  const character = useHeroCharacter({ heroId: gameState.hero?.id ?? null });
  const characterRef = useRef(character);
  characterRef.current = character;

  const [combatEvents, setCombatEvents] = useState<CombatOverlayEvent[]>([]);
  const [session, dispatch] = useReducer(sessionReducer, initialSessionStore);
  const {
    wsConnected,
    wsEverConnected,
    connectionError,
    heroQuests,
    achievements,
    heroSheetOpen,
    heroSheetInitialTab,
    changelogOpen,
    offlineReport,
    needsName,
    activeWorldEvent,
    towns,
    roadNetwork,
    roadSegments,
  } = session;

  const setWsConnected = useCallback((connected: boolean) => {
    dispatch({ type: 'SET_WS_CONNECTED', connected });
  }, []);
  const setConnectionError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_CONNECTION_ERROR', error });
  }, []);
  const setHeroQuests = useCallback((quests: HeroQuest[]) => {
    dispatch({ type: 'SET_HERO_QUESTS', quests });
  }, []);
  const setAchievements = useCallback((a: Achievement[]) => {
    dispatch({ type: 'SET_ACHIEVEMENTS', achievements: a });
  }, []);
  const setChangelogOpen = useCallback((changelog: ChangelogOpen | null) => {
    dispatch({ type: 'SET_CHANGELOG', changelog });
  }, []);
  const setOfflineReport = useCallback((report: OfflineReportData | null) => {
    dispatch({ type: 'SET_OFFLINE_REPORT', report });
  }, []);
  const setNeedsName = useCallback((needs: boolean) => {
    dispatch({ type: 'SET_NEEDS_NAME', needsName: needs });
  }, []);
  const activeWorldEventRef = useRef<ActiveWorldEvent | null>(null);
  const setActiveWorldEvent = useCallback((event: ActiveWorldEvent | null) => {
    // WS packets can arrive before React commits the preceding event.
    activeWorldEventRef.current = event;
    dispatch({ type: 'SET_ACTIVE_WORLD_EVENT', event });
  }, []);
  const setTowns = useCallback((nextTowns: Town[]) => {
    dispatch({ type: 'SET_TOWNS', towns: nextTowns });
  }, []);
  const setRoadNetwork = useCallback((network: Array<Array<{ x: number; y: number }>>, roads?: RoadSegmentPayload[]) => {
    dispatch({ type: 'SET_ROAD_NETWORK', network });
    if (roads) dispatch({ type: 'SET_ROAD_SEGMENTS', segments: roads });
  }, []);
  const setHeroSheetOpen = useCallback((open: boolean, tab?: HeroSheetTab) => {
    dispatch({ type: 'SET_HERO_SHEET_OPEN', open, tab });
  }, []);

  const [buffCooldownEndsAt, setBuffCooldownEndsAt] = useState<
    Partial<Record<BuffType, number>>
  >({});
  const [toast, setToast] = useState<{ message: string; color: string } | null>(null);
  const [merchantPurchaseErrorVersion, setMerchantPurchaseErrorVersion] = useState(0);
  const [logEntries, setLogEntries] = useState<AdventureLogEntry[]>([]);
  /** Live combat narration (mirrors prefixed adventure log lines). */
  const [combatLogLines, setCombatLogLines] = useState<string[]>([]);
  const pendingChangelogRef = useRef<ChangelogOpen | null>(null);
  const logIdCounter = useRef(0);
  const logRawRef = useRef<AdventureLogRawRow[]>([]);
  const i18nForLogRef = useRef({ locale, tr });
  i18nForLogRef.current = { locale, tr };

  const townsRef = useRef<Town[]>([]);
  const roadSettlementsRef = useRef<import('@/network/hero.api').RoadSettlementPayload[]>([]);
  const baseTownDataRef = useRef<import('@/game/types').TownData[]>([]);
  const [currentTown, setCurrentTown] = useState<Town | null>(null);
  const [selectedNPC, setSelectedNPC] = useState<NPC | null>(null);

  // NPC interaction (legacy / non-tour); town tour uses `townTourLastPayload` (NPCInteraction first; NPCDialog only after tap).
  const [nearestNPC, setNearestNPC] = useState<NPCData | null>(null);
  const [npcInteractionDismissed, setNpcInteractionDismissed] = useState<number | null>(null);
  const [townTourLastPayload, setTownTourLastPayload] = useState<TownTourPhasePayload | null>(null);
  const townTourLastPayloadRef = useRef<TownTourPhasePayload | null>(null);
  townTourLastPayloadRef.current = townTourLastPayload;

  const townTourDialogWs = useMemo(
    () => ({
      onInteractionOpened: () => {
        const w = wsRef.current;
        if (w) sendTownTourNPCInteractionOpened(w);
      },
      onDialogAndInteractionClosed: () => {
        const w = wsRef.current;
        if (!w) return;
        const npcId = townTourLastPayloadRef.current?.npcId;
        if (npcId) setNpcInteractionDismissed(npcId);
        setSelectedNPC(null);
        sendTownTourNPCDialogClosed(w);
        sendTownTourNPCInteractionClosed(w);
      },
    }),
    [],
  );

  const [npcShopCosts, setNpcShopCosts] = useState(defaultNpcShopBundle);
  const prevAchievementsRef = useRef<Achievement[]>([]);

  const sheetNowMs = useUiClock(100);

  const latestCrierRef = useRef<CrierAnnouncement | null>(null);
  const crierPresenterRef = useRef(new CrierSpeechPresenter());
  const presentCrierSpeech = useCallback((speech: ActiveCrierSpeech) => {
    crierPresenterRef.current.present(speech, Date.now(), (announcement, remainingMs) => {
      const { locale: loc } = i18nForLogRef.current;
      const shown = engineRef.current?.showCrierAnnouncement(announcement.npcId, announcement.townId, crierAnnouncementText(announcement, loc === 'ru' ? 'ru' : 'en'), remainingMs) ?? false;
      if (shown) latestCrierRef.current = announcement;
      return shown;
    });
  }, []);
  useEffect(() => {
    if (gameState.hero?.activeCrierSpeech) presentCrierSpeech(gameState.hero.activeCrierSpeech);
  }, [gameState.hero, presentCrierSpeech]);
  useEffect(() => {
    engineRef.current?.localizeNarrativeBubbles((key, args) => thoughtText(locale, key, args), latestCrierRef.current ? crierAnnouncementText(latestCrierRef.current, locale === 'ru' ? 'ru' : 'en') : '');
  }, [locale]);

  const appendLogPayload = useCallback((p: AdventureLogLinePayload) => {
    const { locale: loc, tr: bundle } = i18nForLogRef.current;
    logIdCounter.current += 1;
    const id = logIdCounter.current;
    logRawRef.current = appendAdventureLogRawRow(
      logRawRef.current,
      { id, message: p.message ?? '', timestamp: p.atUTC && Number.isFinite(Date.parse(p.atUTC)) ? Date.parse(p.atUTC) : Date.now(), event: p.event, ...(p.atUTC ? { atUTC: p.atUTC } : {}) },
      () => 0,
    );
    setLogEntries(buildAdventureLogEntriesFromRaw([...logRawRef.current], loc, bundle));

    const formattedText = formatAdventureLogPayload(loc, bundle, p);
    if (p.event?.code.startsWith('thought.') && formattedText) engineRef.current?.showHeroThought(p.event.code, formattedText, narrativeBubbleRemainingMs(p.atUTC, Date.now(), 9000), p.atUTC ? `${p.event.code}:${p.atUTC}` : undefined, p.event.args);

    if (isAdventureLogEncounterCode(p.event?.code) && formattedText) {
      setCombatLogLines([formattedText]);
    } else {
      const parsed = parseAdventureLogLine(p.message ?? '');
      if (parsed.type === 'encounter') {
        setCombatLogLines([parsed.title]);
      } else if (parsed.type === 'battle') {
        setCombatLogLines((prev) => [...prev, parsed.text].slice(-5));
      } else if (isAdventureLogCombatCode(p.event?.code) && formattedText) {
        setCombatLogLines((prev) => [...prev, formattedText].slice(-5));
      }
    }
  }, []);

  const appendLogClientMessage = useCallback((rawMessage: string) => {
    const { locale: loc, tr: bundle } = i18nForLogRef.current;
    logIdCounter.current += 1;
    const id = logIdCounter.current;
    logRawRef.current = appendAdventureLogRawRow(
      logRawRef.current,
      { id, message: rawMessage, timestamp: Date.now() },
      () => 0,
    );
    setLogEntries(buildAdventureLogEntriesFromRaw([...logRawRef.current], loc, bundle));
    const parsed = parseAdventureLogLine(rawMessage);
    if (parsed.type === 'encounter') {
      setCombatLogLines([parsed.title]);
    } else if (parsed.type === 'battle') {
      setCombatLogLines((prev) => [...prev, parsed.text].slice(-5));
    }
  }, []);

  useEffect(() => {
    const { locale: loc, tr: bundle } = i18nForLogRef.current;
    setLogEntries(buildAdventureLogEntriesFromRaw([...logRawRef.current], loc, bundle));
  }, [locale, tr]);

  const readerLogEntries = useMemo(() => readerJournal(logEntries, character.journal, locale === 'ru' ? 'ru' : 'en'), [logEntries, character.journal, locale]);

  const adventurePreviewLines = useMemo(
    () =>
      readerLogEntries
        .map((entry) => (entry.kind === 'battle_group' ? entry.title : entry.message))
        .filter((line) => line.trim().length > 0)
        .slice(-7),
    [readerLogEntries],
  );

  useEffect(() => {
    const eng = engineRef.current;
    if (!eng || towns.length === 0) return;
    let cancelled = false;
    (async () => {
      const details = await fetchTownLayoutDetails(towns);
      if (cancelled) return;
      applyTownsToEngine(eng, towns, locale, details);
      const speech = eng.gameState.hero?.activeCrierSpeech;
      if (speech) presentCrierSpeech(speech);
    })();
    return () => {
      cancelled = true;
    };
  }, [locale, towns, presentCrierSpeech]);

  const appendCombatEvent = useCallback((evt: CombatOverlayEvent) => {
    setCombatEvents((prev) => [...prev, evt]);
  }, []);

  const clearCombatEvents = useCallback(() => {
    setCombatEvents([]);
  }, []);

  const expireCombatEvent = useCallback((id: number) => {
    setCombatEvents((prev) => prev.filter((evt) => evt.id !== id));
  }, []);

  const handleCombatAttack = useCallback((p: AttackPayload) => {
    const defender: CombatOverlayEvent['target'] = match(p.source)
        .returnType<CombatOverlayTarget>()
        .with('enemy', () => 'hero')
        .with('hero', () => 'enemy')
        .with('potion', () => 'hero')
        .with('dot', () => 'hero')
        .with('summon', () => 'hero')
        .exhaustive()

    const isBlocked = p.outcome === 'block';
    const isEvaded = p.outcome === 'dodge';
    const isStunned = p.outcome === 'stun';
    const isHealed = p.outcome === 'heal';
    const isCrit = Boolean(p.isCrit);

    const enemyType = engineRef.current?.gameState.enemy?.enemySlug ?? '';
    const combatLogCode =
      p.source === 'potion'
        ? 'log.used_healing_potion'
        : p.source === 'hero'
          ? isStunned
            ? 'log.combat.hero_stun'
            : isEvaded
              ? 'log.combat.hero_dodge'
              : 'log.combat.hero_hit'
          : p.source === 'enemy'
            ? isBlocked
              ? 'log.combat.enemy_block'
              : 'log.combat.enemy_hit'
            : null;
    if (combatLogCode) {
      const { locale: loc, tr: bundle } = i18nForLogRef.current;
      const line = formatAdventureLogEvent(loc, bundle, combatLogCode, {
        enemyType,
        damage: p.damage,
        isCrit,
        debuffType: p.debuffApplied,
        amount: p.damage,
      });
      if (line) setCombatLogLines((prev) => [...prev, line].slice(-5));
    }

    if (isStunned) {
      appendCombatEvent({
        id: Date.now() + Math.random(),
        kind: 'stunned',
        target: 'hero',
        value: 0,
        createdAt: performance.now(),
      });
    } else if (isBlocked || isEvaded) {
      appendCombatEvent({
        id: Date.now() + Math.random(),
        kind: isBlocked ? 'blocked' : 'evaded',
        target: defender,
        value: 0,
        createdAt: performance.now(),
      });
    } else if (isHealed) {
      appendCombatEvent({
        id: Date.now() + Math.random(),
        kind: 'heal',
        target: defender,
        value: p.damage,
        isCrit,
        createdAt: performance.now(),
      });
    } else {
      appendCombatEvent({
        id: Date.now() + Math.random(),
        kind: 'damage',
        target: defender,
        value: p.damage,
        isCrit,
        createdAt: performance.now(),
      });
    }

    const engine = engineRef.current;
    if (!engine) return;

    const isDotLike = p.source === 'dot' || p.source === 'summon';
    if (isDotLike) return;

    if (isStunned) {
      hapticImpact('light');
      engine.camera.shake(3, 120);
      return;
    }

    if (isBlocked || isEvaded) {
      hapticImpact('light');
      engine.camera.shake(3, 120);
      return;
    }

    if (isCrit) {
      hapticImpact('heavy');
      engine.camera.shake(8, 250);
    } else {
      hapticImpact('light');
      engine.camera.shake(4, 150);
    }
  }, [appendCombatEvent]);

  const handleEnemyRegen = useCallback((p: EnemyRegenPayload) => {
    if (p.amount <= 0) return;
    appendCombatEvent({
      id: Date.now() + Math.random(),
      kind: 'regen',
      target: 'enemy',
      value: p.amount,
      createdAt: performance.now(),
    });
  }, [appendCombatEvent]);

  const refreshEquipment = useCallback(() => {
    const owner = engineRef.current;
    const signal = controllerRef.current?.signal;
    if (!owner || !signal || signal.aborted) return;
    const telegramId = getTelegramUserId() ?? 1;
    getHeroEquipment(telegramId)
      .then((eqMap) => {
        if (signal.aborted || engineRef.current !== owner) return;
        const merged: Record<string, EquipmentItem> = {};
        for (const [slot, item] of Object.entries(eqMap)) {
          merged[slot] = {
            id: item.id,
            slot: item.slot ?? slot,
            formId: item.formId ?? '',
            name: item.name,
            rarity: (item.rarity?.toLowerCase() ?? 'common') as EquipmentItem['rarity'],
            ilvl: item.ilvl ?? 1,
            primaryStat: item.primaryStat ?? 0,
            statType: item.statType ?? 'mixed',
          };
        }
        const engine = engineRef.current;
        if (engine?.gameState.hero) {
          const prevEquip = engine.gameState.hero.equipment ?? {};
          const combined = { ...prevEquip, ...merged };
          controllerRef.current?.patchHero({ equipment: combined });
        }
        setGameState((prev) => {
          if (!prev.hero) return prev;
          const prevEquip = prev.hero.equipment ?? {};
          return { ...prev, hero: { ...prev.hero, equipment: { ...prevEquip, ...merged } } };
        });
      })
      .catch(() => console.warn('[App] Could not fetch equipment'));
  }, []);

  const refreshHeroQuests = useCallback(() => {
    const owner = engineRef.current;
    const signal = controllerRef.current?.signal;
    if (!owner || !signal || signal.aborted) return;
    const telegramId = getTelegramUserId() ?? 1;
    getHeroQuests(telegramId)
      .then((q) => { if (!signal.aborted && engineRef.current === owner) setHeroQuests(q); })
      .catch(() => console.warn('[App] Could not refresh hero quests'));
  }, [setHeroQuests]);

  // Initialize engine and WebSocket
  useEffect(() => {
    const container = canvasRef.current;
    if (!container) return;

    const controller = new GameSessionController({
      telegramId: getTelegramUserId() ?? 1,
      onState: setGameState,
      onConnection: (state) => {
        setWsConnected(state === 'connected');
        if (state === 'connected') void characterRef.current.loadJournal();
      },
      onError: setConnectionError,
      onProximity: (npc) => setNearestNPC((previous) => previous === npc ? previous : npc),
    });
    controllerRef.current = controller;
    const engine = controller.engine;
    const ws = controller.socket;
    const isActive = controller.isActive;
    engineRef.current = engine;
    wsRef.current = ws;

    // Shared context handed to every world event handler (see world-events/registry).
    const worldEventCtx = {
      ws: () => wsRef.current,
      engine: () => engineRef.current,
      hero: () => engineRef.current?.gameState.hero ?? null,
      locale: (): WorldEventLocaleSnapshot => i18nForLogRef.current,
      setActive: setActiveWorldEvent,
      getActive: () => activeWorldEventRef.current,
      appendLogClientMessage,
      refreshEquipment,
      triggerHaptic: (kind: 'success' | 'warning' | 'error') => hapticNotification(kind),
    };

    // Wire WS handler -- routes server messages to engine + UI callbacks
    controller.wire({
      onCombatStart: () => {
        setCombatLogLines([]);
        clearCombatEvents();
      },
      onCombatAttack: handleCombatAttack,
      onEnemyRegen: handleEnemyRegen,

      onHeroStateReceived: () => {
        const heroState = engine.gameState.hero;
        if (!heroState) return;
        // hero_state is a complete snapshot; REST patches do not enter this callback.
        if (activeWorldEventRef.current?.kind === 'socialPass' &&
          (heroState.excursionKind !== 'hero_meet' || heroState.excursionPhase?.toLowerCase() === 'return')) {
          setActiveWorldEvent(null);
          engine.clearWorldEventPartnerOverlay();
        }
        if (
          heroState.excursionKind !== 'town' && heroState.excursionKind !== 'road_poi' &&
          townTourLastPayloadRef.current
        ) {
          setTownTourLastPayload(null);
          setSelectedNPC(null);
          setNpcInteractionDismissed(null);
        }
      },

      onCombatEnd: (p) => {
        const bundle = i18nForLogRef.current.tr;
        setCombatLogLines([]);
        clearCombatEvents();
        hapticNotification('success');

        const parts: string[] = [];
        if (p.xpGained > 0) parts.push(t(bundle.toastGainedXp, { xp: p.xpGained }));
        if (p.goldGained > 0) parts.push(t(bundle.toastGainedGold, { gold: p.goldGained }));
        const equipDrop = (p.loot ?? []).find(
          (l) => l.itemType !== 'gold' && l.itemType !== 'potion',
        );
        if (equipDrop?.name) parts.push(t(bundle.toastFoundItem, { name: equipDrop.name }));
        // Victory line comes from server adventure log (Defeated …) + WS adventure_log_line

        if (p.leveledUp && p.newLevel) {
          setToast({ message: t(bundle.levelUp, { level: p.newLevel }), color: '#ffd700' });
          hapticNotification('success');
        }

        // Refresh canonical rewards after combat. Achievements remain stored server-side,
        // but their unfinished interface and notifications stay hidden.
        const tid = getTelegramUserId() ?? 1;
        getHeroQuests(tid).then((q) => { if (isActive()) setHeroQuests(q); }).catch(() => {});
        refreshEquipment();
        getAchievements(tid).then((a) => {
          if (!isActive()) return;
          prevAchievementsRef.current = a;
          setAchievements(a);
        }).catch(() => {});
      },

      onHeroDied: (_p) => {
        hapticNotification('error');
        // Death line comes from server log + WS
      },

      onHeroRevived: () => {
        setCombatLogLines([]);
        clearCombatEvents();
        setToast({ message: i18nForLogRef.current.tr.heroRevived, color: '#44cc44' });
        // "Hero revived" comes from server log + WS
      },

      onBuffApplied: (_p) => {
        // Buff activation comes from server log + WS
      },

      onTownEnter: (p) => {
        const { locale: loc, tr: bundle } = i18nForLogRef.current;
        const town = townsRef.current.find((t) => t.id === p.townId) ?? null;
        setCurrentTown(town);
        const townDisp = townLabel(loc, p.townNameKey, p.townName);
        setToast({ message: t(bundle.entering, { townName: townDisp }), color: '#daa520' });
        // The committed town.arrival log is the journal source; this frame only updates the town UI.
        setNearestNPC(null);
        setSelectedNPC(null);
        setNpcInteractionDismissed(null);
        setTownTourLastPayload(null);
      },

      onCrierAnnouncement: (p) => {
        const clock = parseCrierSpeechClock(p);
        if (clock) {
          presentCrierSpeech({ ...clock, announcement: p });
          return;
        }
        if (p.occurrenceId || p.atUTC || p.expiresAtUTC) return;
        latestCrierRef.current = p;
        const { locale: loc } = i18nForLogRef.current;
        engine.showCrierAnnouncement(p.npcId, p.townId, crierAnnouncementText(p, loc === 'ru' ? 'ru' : 'en'));
      },

      onAdventureLogLine: (p) => {
        appendLogPayload(p);
      },

      onTownNPCVisit: () => {
        // Town NPC UI is driven by `town_tour_phase` (engine still gets lines via ws-handler).
      },

      onTownTourPhase: (p) => {
        setTownTourLastPayload(p);
        const ph = p.phase;
        if (ph === 'npc_welcome' || ph === 'npc_service') {
          setNpcInteractionDismissed(null);
        }
        if (ph === 'wander' || ph === 'rest' || ph === 'npc_approach') {
          setSelectedNPC(null);
        }
      },

      onTownTourServiceEnd: () => {
        setTownTourLastPayload(null);
        setSelectedNPC(null);
      },

      onTownExit: () => {
        setCurrentTown(null);
        setNearestNPC(null);
        setTownTourLastPayload(null);
      },

      onCharacterState: (p) => characterRef.current.applySnapshot(p),
      onNarrativeAdvice: (p) => characterRef.current.applyAdvice(p),
      onNarrativeDecision: (p) => characterRef.current.applyDecision(p),
      onWorldEventStart: (p) => {
        const h = getWorldEventHandler(p.eventId);
        if (h) h.onStart(p, worldEventCtx);
      },

      onWorldEventLine: (p) => {
        // Active event handler decides which line type to render.
        const active = activeWorldEventRef.current;
        if (!active) return;
        const h = getWorldEventHandler(active.eventId);
        if (h) h.onLine(p, worldEventCtx);
      },

      onWorldEventReward: (p) => {
        const active = activeWorldEventRef.current;
        const eventId = active?.eventId;
        if (!eventId) return;
        const h = getWorldEventHandler(eventId);
        h?.onReward?.(p, worldEventCtx);
      },

      onWorldEventEnd: (p) => {
        const h = getWorldEventHandler(p.eventId);
        if (h) h.onEnd(p, worldEventCtx);
      },

      onLevelUp: (p) => {
        setToast({ message: t(i18nForLogRef.current.tr.levelUp, { level: p.newLevel }), color: '#ffd700' });
        hapticNotification('success');
        // Level-up lines come from server log + WS
      },

      onEquipmentChange: (p) => {
        const bundle = i18nForLogRef.current.tr;
        setToast({ message: t(bundle.newEquipment, { slot: p.slot, itemName: p.item.name }), color: '#cc88ff' });
        // Equipment line comes from server log + WS
        refreshEquipment();
      },

      onPotionCollected: (p) => {
        const bundle = i18nForLogRef.current.tr;
        setToast({ message: t(bundle.potionsCollected, { count: p.count }), color: '#44cc44' });
      },

      onQuestProgress: (p) => {
        const bundle = i18nForLogRef.current.tr;
        dispatch({ type: 'UPDATE_QUEST_PROGRESS', questId: p.questId, progress: p.current });
        if (p.title) {
          setToast({
            message: t(bundle.questProgress, { title: p.title, current: p.current, target: p.target }),
            color: '#44aaff',
          });
        }
      },

      onQuestComplete: (p) => {
        const bundle = i18nForLogRef.current.tr;
        dispatch({ type: 'COMPLETE_QUEST', questId: p.questId });
        setToast({ message: t(bundle.questCompleted, { title: p.title }), color: '#ffd700' });
        hapticNotification('success');
      },

      onError: (p) => {
        if (p.code === 'merchant_purchase_pending' || p.code === 'merchant_purchase_failed') {
          setMerchantPurchaseErrorVersion((version) => version + 1);
        }
        const message = p.code === 'merchant_purchase_pending'
          ? i18nForLogRef.current.tr.purchasePending
          : p.code === 'merchant_purchase_outcome_unknown'
            ? i18nForLogRef.current.tr.purchaseOutcomeUnknown
            : p.message;
        setToast({ message, color: '#ff4444' });
      },

      onExcursionEnd: () => {
        if (activeWorldEventRef.current?.kind === 'socialPass') {
          setActiveWorldEvent(null);
          engine.clearWorldEventPartnerOverlay();
        }
        setTownTourLastPayload(null);
        setSelectedNPC(null);
        setNpcInteractionDismissed(null);
      },
    }, {
      locale: i18nForLogRef.current.locale,
      getRoadSettlements: () => roadSettlementsRef.current,
      getBaseTownData: () => baseTownDataRef.current,
    });

    void controller.mount(container, (owner) => bootstrapSession(owner, {
      setNpcShopCosts, pendingChangelogRef, setChangelogOpen, setNeedsName, i18nForLogRef,
      baseTownDataRef, roadSettlementsRef, setTowns, townsRef, setRoadNetwork, setOfflineReport,
      setHeroQuests, refreshEquipment, prevAchievementsRef, setAchievements, logRawRef, logIdCounter, setLogEntries,
    }));
    return () => {
      controller.dispose();
      crierPresenterRef.current = new CrierSpeechPresenter();
      latestCrierRef.current = null;
      if (controllerRef.current === controller) controllerRef.current = null;
      if (engineRef.current === engine) engineRef.current = null;
      if (wsRef.current === ws) wsRef.current = null;
    };
  }, []);

  // Restore per-hero buff button cooldowns
  useEffect(() => {
    const id = gameState.hero?.id;
    if (id == null) return;
    const loaded = loadCooldownsFromStorage(id);
    if (Object.keys(loaded).length === 0) return;
    setBuffCooldownEndsAt((prev) => ({ ...loaded, ...prev }));
  }, [gameState.hero?.id]);

  // ---- Handlers ----

  const handleBuffActivate = useCallback((type: BuffType) => {
    runBuffActivate(type, {
      engine: engineRef.current,
      ws: wsRef.current,
      tr,
      setToast,
      setBuffCooldownEndsAt,
    });
  }, [tr]);

  const handleRevive = useCallback(() => {
    if (controllerRef.current?.revive()) return;
    setToast({ message: tr.reconnecting, color: '#ffcc66' });
  }, [tr.reconnecting]);

  const handleQuestClaim = useCallback(
    (heroQuestId: number) => {
      const owner = controllerRef.current;
      if (!owner) return;
      const telegramId = getTelegramUserId() ?? 1;
      void owner.request(
        () => claimQuest(heroQuestId, telegramId),
        (hero) => {
          owner.applyHeroPatch(hero);
          setToast({ message: tr.questRewardsClaimed, color: '#ffd700' });
          hapticNotification('success');
          refreshHeroQuests();
        },
        (err) => {
          console.warn('[App] Failed to claim quest:', err);
          setToast({ message: tr.failedToClaimRewards, color: '#ff4444' });
        },
      );
    },
    [refreshHeroQuests],
  );

  const handleQuestAbandon = useCallback(
    (heroQuestId: number) => {
      const owner = controllerRef.current;
      if (!owner) return;
      const telegramId = getTelegramUserId() ?? 1;
      void owner.request(
        () => abandonQuest(heroQuestId, telegramId),
        () => {
          setToast({ message: tr.questAbandoned, color: '#ff8844' });
          refreshHeroQuests();
        },
        (err) => {
          console.warn('[App] Failed to abandon quest:', err);
          setToast({ message: tr.failedToAbandonQuest, color: '#ff4444' });
        },
      );
    },
    [refreshHeroQuests],
  );

  const handleNPCHeroUpdated = useCallback((hero: HeroResponse) => {
    controllerRef.current?.applyHeroPatch(hero);
  }, []);

  const handleEquipmentAdviceUpdated = useCallback((hero: HeroResponse) => {
    controllerRef.current?.applyEquipmentAdvice(hero);
  }, []);

  const handleNameSet = useCallback((hero: HeroResponse) => {
    const owner = controllerRef.current;
    if (!owner) return;
    completeNamedSession(owner, hero, {
      setNpcShopCosts, pendingChangelogRef, setChangelogOpen, setNeedsName, i18nForLogRef,
      baseTownDataRef, roadSettlementsRef, setTowns, townsRef, setRoadNetwork, setOfflineReport,
      setHeroQuests, refreshEquipment, prevAchievementsRef, setAchievements, logRawRef, logIdCounter, setLogEntries,
    });
  }, []);

  const handleDismissChangelog = useCallback(() => {
    setChangelogOpen(null);
    const telegramId = getTelegramUserId() ?? 1;
    ackChangelog(telegramId).catch(() => console.warn('[App] changelog ack failed'));
  }, []);

  const handleUsePotion = useCallback(() => {
    if (!controllerRef.current?.usePotion()) return;

    hapticImpact('medium');
    // Server logs potion use + sends adventure_log_line
  }, []);

  // ---- NPC Interaction Handlers ----

  const handleNPCViewQuests = useCallback((npc: NPCData) => {
    const matchedNPC: NPC = {
      id: npc.id,
      townId: npc.townId,
      name: npc.name,
      nameKey: npc.nameKey,
      type: npc.type,
      offsetX: 0,
      offsetY: 0,
      townLevelMin: npc.townLevelMin,
      townLevelMax: npc.townLevelMax,
    };
    setSelectedNPC(matchedNPC);
    setNpcInteractionDismissed(npc.id);
  }, []);

  const handleTownTourInteractionDismiss = useCallback(() => {
    const w = wsRef.current;
    if (!w) return;
    const ph = townTourLastPayload?.phase;
    const npcId = townTourLastPayload?.npcId;
    if (npcId) setNpcInteractionDismissed(npcId);
    if (ph === 'npc_welcome') {
      sendTownTourNPCDialogClosed(w);
    } else if (ph === 'npc_service') {
      sendTownTourNPCInteractionClosed(w);
    }
  }, [townTourLastPayload?.phase, townTourLastPayload?.npcId]);

  // ---- Wandering NPC popup actions (route through generic world_event_action) ----

  const handleWanderingBuySlot = useCallback((slotIndex: number) => {
    const ws = wsRef.current;
    const active = activeWorldEventRef.current;
    if (ws && active?.kind === 'questAlms') {
      sendWorldEventAction(ws, active.sessionId, 'buy_slot', { slotIndex });
    }
  }, []);

  const handleWanderingLeave = useCallback(() => {
    const ws = wsRef.current;
    const active = activeWorldEventRef.current;
    if (ws && active?.kind === 'questAlms') {
      sendWorldEventAction(ws, active.sessionId, 'leave');
    }
  }, []);

  const handleWanderingAccept = useCallback(() => {
    handleWanderingBuySlot(0);
  }, [handleWanderingBuySlot]);

  const handleWanderingDecline = useCallback(() => {
    handleWanderingLeave();
  }, [handleWanderingLeave]);

  const handleWildAction = useCallback((action: string) => {
    const ws = wsRef.current;
    const active = activeWorldEventRef.current;
    if (ws && active?.kind === 'wild') {
      sendWorldEventAction(ws, active.sessionId, action);
    }
  }, []);

  const handleRoadPOIAction = useCallback((action: string) => {
    const ws = wsRef.current;
    const active = activeWorldEventRef.current;
    if (ws && active?.kind === 'roadPoi') {
      sendWorldEventAction(ws, active.sessionId, action);
      if (action === 'accept' || action === 'decline' || action === 'leave') {
        setActiveWorldEvent(null);
      }
    }
  }, [setActiveWorldEvent]);

  const handlePurchaseBuffRefill = useCallback((_type: BuffType, invoiceUrl: string) => {
    const owner = controllerRef.current;
    const sdk = getTelegramWebApp();
    if (!owner || !sdk?.openInvoice) return;
    const telegramId = getTelegramUserId() ?? undefined;
    owner.openPaymentInvoice(invoiceUrl, sdk.openInvoice.bind(sdk), (signal) => getHero(telegramId, signal));
  }, []);

  const handleHeroMeetSendMessage = useCallback((text: string) => {
    const ws = wsRef.current;
    const sessionId = activeWorldEventRef.current?.sessionId;
    if (ws && sessionId) {
      sendWorldEventAction(ws, sessionId, 'send_message', { text });
    }
  }, []);

  const handleHeroMeetEndConversation = useCallback(() => {
    const ws = wsRef.current;
    const sessionId = activeWorldEventRef.current?.sessionId;
    if (ws && sessionId) {
      sendWorldEventAction(ws, sessionId, 'end_conversation');
    }
  }, []);

  const openHeroSheet = useCallback((tab?: HeroSheetTab) => {
    setHeroSheetOpen(true, tab ?? 'stats');
  }, [setHeroSheetOpen]);

  const closeHeroSheet = useCallback(() => {
    setHeroSheetOpen(false);
  }, [setHeroSheetOpen]);

  const heroOnExcursionTour =
    gameState.hero?.excursionKind === 'town' || gameState.hero?.excursionKind === 'road_poi';

  const heroOnTownTour = heroOnExcursionTour;

  const townTourChipActive =
    (townTourLastPayload?.phase === 'npc_welcome' || townTourLastPayload?.phase === 'npc_service') &&
    (townTourLastPayload.npcId ?? 0) !== 0;

  const townTourInteractionNPC =
    townTourChipActive && townTourLastPayload
      ? townTourPayloadToNPCData(townTourLastPayload, locale, currentTown)
      : null;

  const legacyProximityNPC = !heroOnExcursionTour ? nearestNPC : null;

  const interactionNpc = townTourInteractionNPC ?? legacyProximityNPC;

  const showNPCInteraction =
    interactionNpc != null &&
    npcInteractionDismissed !== interactionNpc.id &&
    (gameState.phase === GamePhase.Walking || gameState.phase === GamePhase.InTown) &&
    !selectedNPC;

  const dialogNpc = selectedNPC;

  const handleNPCInteractionDismiss = useCallback(() => {
    if (interactionNpc) setNpcInteractionDismissed(interactionNpc.id);
  }, [interactionNpc]);

  const completedQuestCount = useMemo(
    () =>
      heroQuests.filter((q) => q.status === 'completed').length,
    [heroQuests],
  );

  const dismissToast = useCallback(() => setToast(null), []);
  const openAdventureLog = useCallback(() => {
    setHeroSheetOpen(true, 'journal');
  }, [setHeroSheetOpen]);

  const questClaimDisabled =
    gameState.phase === GamePhase.Dead || (gameState.hero?.hp ?? 0) <= 0;
  const inCombatLogMode =
    gameState.phase === GamePhase.Fighting || gameState.phase === GamePhase.Dead;
  const nearHeroLogLines = inCombatLogMode ? combatLogLines : adventurePreviewLines;

  return {
    character,
    characterLanguage: locale === 'ru' ? 'ru' as const : 'en' as const,
    refs: { canvasRef, engineRef, wsRef },
    state: {
      gameState,
      combatEvents,
      wsConnected,
      wsEverConnected,
      buffCooldownEndsAt,
      connectionError,
      toast,
      merchantPurchaseErrorVersion,
      logEntries: readerLogEntries,
      combatLogLines,
      offlineReport,
      needsName,
      changelogOpen,
      towns,
      roadNetwork,
      roadSegments,
      heroQuests,
      currentTown,
      selectedNPC,
      heroSheetOpen,
      heroSheetInitialTab,
      nearestNPC,
      npcInteractionDismissed,
      townTourLastPayload,
      activeWorldEvent,
      npcShopCosts,
      achievements,
      sheetNowMs,
      adventurePreviewLines,
      completedQuestCount,
      questClaimDisabled,
      inCombatLogMode,
      nearHeroLogLines,
      heroOnTownTour,
      townTourChipActive,
      townTourInteractionNPC,
      interactionNpc,
      showNPCInteraction,
      dialogNpc,
      townTourDialogWs,
    },
    actions: {
      handleBuffActivate,
      handleRevive,
      handleUsePotion,
      handleQuestClaim,
      handleQuestAbandon,
      handleNPCHeroUpdated,
      handleEquipmentAdviceUpdated,
      handleNameSet,
      handleDismissChangelog,
      handleNPCViewQuests,
      handleTownTourInteractionDismiss,
      handleNPCInteractionDismiss,
      handleWanderingAccept,
      handleWanderingDecline,
      handleWanderingBuySlot,
      handleWanderingLeave,
      handleWildAction,
      handleRoadPOIAction,
      handlePurchaseBuffRefill,
      handleHeroMeetSendMessage,
      handleHeroMeetEndConversation,
      refreshHeroQuests,
      refreshEquipment,
      dismissToast,
      openAdventureLog,
      openHeroSheet,
      closeHeroSheet,
      setSelectedNPC,
      setToast,
      setOfflineReport,
      expireCombatEvent,
    },
    translations: tr,
  };
}
